import type { InjectionKey, Ref } from 'vue'
import type { Stakeholder } from '../Stakeholder'
import type {
  ChangeStakeholderInfoRequestPayload,
  ChangeStakeholderInternalIdentificationNumbersRequestPayload,
  ChangeStakeholderBusinessNumbersRequestPayload,
  ChangeStakeholderPostalAddressRequestPayload,
  ChangeStakeholderVisitAddressRequestPayload,
  ChangeStakeholderBillingDetailsRequestPayload
} from '../api-requests/StakeholdersRequests'

/**
 * Business-logic commands for a single stakeholder. Each command owns its
 * full write cycle — loading toggle, command-id generation, the API call,
 * and applying the returned `newState` back into the datasource. Views call
 * these instead of issuing API requests and orchestrating refetches
 * themselves.
 */
export interface StakeholderCommands {
  /**
   * Change the general info. When the stakeholder type has custom fields,
   * pass the edited `customFieldValues` — the command patches them after the
   * general-info PUT (mirroring the previous modal-side behaviour).
   */
  changeGeneralInfo(
    id: string,
    payload: ChangeStakeholderInfoRequestPayload,
    customFieldValues?: Record<string, unknown>
  ): Promise<void>
  changeInternalIdentificationNumbers(
    id: string,
    payload: ChangeStakeholderInternalIdentificationNumbersRequestPayload
  ): Promise<void>
  changeOfficialBusinessNumbers(
    id: string,
    payload: ChangeStakeholderBusinessNumbersRequestPayload
  ): Promise<void>
  changePostalAddress(
    id: string,
    payload: ChangeStakeholderPostalAddressRequestPayload
  ): Promise<void>
  changeVisitAddress(
    id: string,
    payload: ChangeStakeholderVisitAddressRequestPayload
  ): Promise<void>
  /**
   * Change billing details. Both the billing-details and the billing-address
   * editors funnel through this command — each builds a full
   * `ChangeStakeholderBillingDetailsRequestPayload` (echoing the fields it
   * does not touch).
   */
  changeBillingDetails(
    id: string,
    payload: ChangeStakeholderBillingDetailsRequestPayload
  ): Promise<void>
  remove(id: string): Promise<void>
  restore(id: string): Promise<void>
}

export interface StakeholderService {
  stakeholder: Ref<Stakeholder | undefined>
  customFieldValues: Ref<Record<string, unknown>>
  isLoading: Ref<boolean>
  fetchStakeholder(
    id: string,
    options?: { forceFetch?: boolean }
  ): Promise<void>
  fetchCustomFieldValues(id: string): Promise<void>
  setStakeholder(stakeholder: Stakeholder): void
  setCustomFieldValues(values: Record<string, unknown>): void
  subscribeToStakeholderEvents(): void
  unsubscribeFromStakeholderEvents(): void
  commands: StakeholderCommands
}

export const stakeholderKey = Symbol(
  'gs-stakeholder'
) as InjectionKey<StakeholderService>
